import { FormGroup } from '@angular/forms';

/** Acción: mapa numérico → valor de otro campo (p. ej. cuantía → Impacto). */
export interface ComputedRuleRangeAction {
  kind: 'ranges';
  source: string;
  target: string;
  ranges: Array<{
    value: string;
    min?: number;
    max?: number;
    lt?: number;
    lte?: number;
    gt?: number;
    gte?: number;
  }>;
}

/** Acción: matriz fila × columna (nombres exactos de opciones de select). */
export interface ComputedRuleMatrixAction {
  kind: 'matrix';
  rowSource: string;
  colSource: string;
  target: string;
  matrix: Record<string, Record<string, string>>;
}

/** Entrada de mapa cuando el lookup calcula monto = cuantía × percent / 100 (percent = mínimo del rango). */
export interface LookupAmountFromCuantiaEntry {
  percent: number;
  /** Texto para `field.hint` (tooltip / ngbPopover en la UI). */
  tooltip?: string;
  /** Solo informativo (p. ej. 50 en "30 a 50"); no afecta el cálculo si usas percent como mínimo. */
  percentMax?: number;
}

/** Acción: valor de un campo → otro vía mapa literal, o monto calculado desde otro campo numérico. */
export interface ComputedRuleLookupAction {
  kind: 'lookup';
  source: string;
  target: string;
  /**
   * Mapa: clave = valor del `source` (p. ej. nivel de riesgo).
   * - Valor string: se asigna al target (select), comportamiento legacy.
   * - Valor objeto `{ percent, tooltip? }`: requiere `amountSource`; el target recibe número (cuantía × percent / 100).
   */
  map: Record<string, string | LookupAmountFromCuantiaEntry>;
  /** Nombre exacto del customfield numérico (p. ej. "Cuantía") para calcular provisión. */
  amountSource?: string;
  /** Alias snake_case por si el JSON en BD usa `amount_source`. */
  amount_source?: string;
}

export type ComputedRuleAction =
  | ComputedRuleRangeAction
  | ComputedRuleMatrixAction
  | ComputedRuleLookupAction;

export interface ComputedRuleBlock {
  id?: string;
  when?: string[];
  actions: ComputedRuleAction[];
}

export interface ComputedRulesDocument {
  version?: number;
  rules: ComputedRuleBlock[];
}

function cleanJsonString(raw: string): string {
  let s = raw.trim();
  if ((s.startsWith("'") && s.endsWith("'")) || (s.startsWith('"') && s.endsWith('"'))) {
    s = s.slice(1, -1);
  }
  return s.replace(/\\'/g, "'").replace(/\\"/g, '"');
}

export function parseComputedRules(raw: unknown): ComputedRulesDocument | null {
  if (raw == null || raw === '') return null;
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(cleanJsonString(raw));
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object') return null;
  const rules = (obj as ComputedRulesDocument).rules;
  if (!Array.isArray(rules) || rules.length === 0) return null;
  return { version: (obj as ComputedRulesDocument).version ?? 1, rules };
}

export function collectMergedRulesFromFields(fields: unknown[]): ComputedRulesDocument | null {
  if (!Array.isArray(fields)) return null;
  const byId = new Map<string, ComputedRuleBlock>();
  let anon = 0;
  for (const f of fields) {
    const doc = parseComputedRules((f as { computed_rules?: unknown })?.computed_rules);
    if (!doc?.rules?.length) continue;
    for (const r of doc.rules) {
      const key =
        r.id != null && String(r.id).trim() !== ''
          ? String(r.id)
          : `__anon_${anon++}`;
      if (!byId.has(key)) {
        byId.set(key, r);
      }
    }
  }
  if (!byId.size) return null;
  return { version: 1, rules: [...byId.values()] };
}

export function findFieldByExactFieldName(
  fields: Array<{ id?: unknown; name?: unknown }>,
  exactName: string
): { id: number; name: string } | null {
  if (!Array.isArray(fields) || !exactName) return null;
  const hit = fields.find((f) => f && String(f.name) === exactName);
  if (!hit || hit.id == null) return null;
  const id = Number(hit.id);
  if (Number.isNaN(id)) return null;
  return { id, name: String(hit.name) };
}

function findFieldDefById(fields: unknown[], fieldId: number): any | null {
  if (!Array.isArray(fields)) return null;
  return fields.find((f: any) => f && Number(f.id) === fieldId) ?? null;
}

/** Opciones de select como string (soporta values = ["a"] o [{ name: "a" }]). */
function selectOptionsAsStrings(fieldDef: any): string[] {
  const values = fieldDef?.values;
  if (!Array.isArray(values)) return [];
  return values.map((v: any) =>
    typeof v === 'string' ? v : v != null && v.name != null ? String(v.name) : String(v)
  );
}

/**
 * Ajusta el valor al literal exacto de una opción del select (evita vacío si en BD hay " Bajo" con espacio).
 */
export function alignValueToSelectOptions(fieldDef: any, raw: unknown): unknown {
  if (raw == null || raw === '') return raw;
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw) && !(raw instanceof Date)) {
    return null;
  }
  const want = String(raw);
  const opts = selectOptionsAsStrings(fieldDef);
  if (!opts.length) return raw;
  const exact = opts.find((o) => o === want);
  if (exact !== undefined) return exact;
  const byTrim = opts.find((o) => o.trim() === want.trim());
  if (byTrim !== undefined) return byTrim;
  return raw;
}

function getMatrixRow(
  matrix: Record<string, Record<string, string>> | undefined,
  rowVal: unknown
): Record<string, string> | undefined {
  if (!matrix || rowVal == null || rowVal === '') return undefined;
  const rs = String(rowVal);
  if (matrix[rs]) return matrix[rs];
  const t = rs.trim();
  const rowKey = Object.keys(matrix).find((k) => k === t || k.trim() === t);
  return rowKey != null ? matrix[rowKey] : undefined;
}

function getMatrixCell(
  rowMap: Record<string, string> | undefined,
  colVal: unknown
): string | undefined {
  if (!rowMap || colVal == null || colVal === '') return undefined;
  const cs = String(colVal);
  if (rowMap[cs] !== undefined) {
    const v = rowMap[cs];
    return typeof v === 'string' ? v : undefined;
  }
  const t = cs.trim();
  const colKey = Object.keys(rowMap).find((k) => k === t || k.trim() === t);
  if (colKey == null) return undefined;
  const v = rowMap[colKey];
  return typeof v === 'string' ? v : undefined;
}

function lookupAmountSourceName(act: ComputedRuleLookupAction): string | undefined {
  const s = act.amountSource ?? act.amount_source;
  if (s == null || String(s).trim() === '') return undefined;
  return String(s).trim();
}

function coalesceLookupMapValue(
  mapped: string | LookupAmountFromCuantiaEntry | undefined
): string | LookupAmountFromCuantiaEntry | undefined {
  if (mapped == null || typeof mapped !== 'string') return mapped;
  const t = mapped.trim();
  if (t.startsWith('{') && t.endsWith('}')) {
    try {
      const parsed = JSON.parse(t) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as LookupAmountFromCuantiaEntry;
      }
    } catch {
      /* mantener string */
    }
  }
  return mapped;
}

function resolveLookupMapEntry(
  map: Record<string, string | LookupAmountFromCuantiaEntry> | undefined,
  keyRaw: string
): string | LookupAmountFromCuantiaEntry | undefined {
  if (!map) return undefined;
  const ks = String(keyRaw);
  if (map[ks] !== undefined) return map[ks];
  if (map[ks.trim()] !== undefined) return map[ks.trim()];
  const t = ks.trim();
  const mk = Object.keys(map).find((k) => k.trim() === t);
  return mk != null ? map[mk] : undefined;
}

function getControl(form: FormGroup, fieldId: number) {
  return form.get(String(fieldId));
}

function readScalarValue(form: FormGroup, fieldId: number): unknown {
  const c = getControl(form, fieldId);
  return c ? c.value : undefined;
}

function toNumberForRanges(v: unknown): number {
  if (v === null || v === undefined || v === '') return NaN;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v != null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) return NaN;

  let s = String(v)
    .trim()
    .replace(/\$/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s/g, '')
    .replace(/'/g, '');
  if (!s) return NaN;

  // Formato con coma decimal: 1.234.567,89 o 1234,89
  const withDecimalComma = /^([\d.]+),(\d{1,3})$/.exec(s);
  if (withDecimalComma) {
    const intPart = withDecimalComma[1].replace(/\./g, '');
    const n = parseFloat(`${intPart}.${withDecimalComma[2]}`);
    return Number.isFinite(n) ? n : NaN;
  }

  // Varios puntos => miles (p. ej. Chile CLP: 45.554.499)
  const dotCount = (s.match(/\./g) || []).length;
  if (dotCount >= 2) {
    const n = parseFloat(s.replace(/\./g, ''));
    return Number.isFinite(n) ? n : NaN;
  }

  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
}

function matchesRange(n: number, r: ComputedRuleRangeAction['ranges'][0]): boolean {
  if (Number.isNaN(n)) return false;
  if (r.lt != null && !(n < r.lt)) return false;
  if (r.lte != null && !(n <= r.lte)) return false;
  if (r.gt != null && !(n > r.gt)) return false;
  if (r.gte != null && !(n >= r.gte)) return false;
  if (r.min != null && n < r.min) return false;
  if (r.max != null && n >= r.max) return false;
  return true;
}

function pickRangeValue(n: number, ranges: ComputedRuleRangeAction['ranges']): string | null {
  for (const r of ranges) {
    if (matchesRange(n, r)) return r.value;
  }
  return null;
}

function setScalarIfChanged(
  form: FormGroup,
  fields: unknown[],
  fieldId: number,
  rawValue: unknown,
  emitEvent: boolean
): void {
  const c = getControl(form, fieldId);
  if (!c) return;
  const fieldDef = findFieldDefById(fields, fieldId);
  const value = normalizeValueForControl(fieldDef, rawValue);
  if (value === undefined) {
    return;
  }
  const cur = c.value;
  if (cur === value) return;
  if (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    cur !== '' &&
    cur != null &&
    !(typeof cur === 'string' && cur.trim() === '') &&
    Number(cur) === value
  ) {
    return;
  }
  c.setValue(value, { emitEvent });
}

/** Evita asignar objetos a <input type="number"> (error "[object Object] cannot be parsed"). */
function normalizeValueForControl(fieldDef: any, raw: unknown): unknown {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw) && !(raw instanceof Date)) {
    return null;
  }
  if (!fieldDef) return raw;
  const t = Number(fieldDef.type);
  if (t === 1) {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    const n = toNumberForRanges(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (t === 2 && typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return raw;
}

/**
 * Ejecuta en orden todos los bloques y acciones del documento.
 * `fields`: lista de definiciones del mismo formulario (nombres exactos, p. ej. "Resultado ").
 */
export function executeComputedRules(form: FormGroup, fields: unknown[], doc: ComputedRulesDocument): void {
  if (!form || !doc?.rules?.length) return;

  for (const rule of doc.rules) {
    for (const action of rule.actions || []) {
      if (!action || typeof action !== 'object') continue;

      if (action.kind === 'ranges') {
        const src = findFieldByExactFieldName(fields as any[], action.source);
        const tgt = findFieldByExactFieldName(fields as any[], action.target);
        if (!src || !tgt) continue;
        const n = toNumberForRanges(readScalarValue(form, src.id));
        const next = pickRangeValue(n, action.ranges || []);
        if (next != null) {
          const tgtDef = findFieldDefById(fields, tgt.id);
          const aligned = alignValueToSelectOptions(tgtDef, next);
          setScalarIfChanged(form, fields, tgt.id, aligned, false);
        }
      } else if (action.kind === 'matrix') {
        const rowF = findFieldByExactFieldName(fields as any[], action.rowSource);
        const colF = findFieldByExactFieldName(fields as any[], action.colSource);
        const tgt = findFieldByExactFieldName(fields as any[], action.target);
        if (!rowF || !colF || !tgt) continue;
        const rowVal = readScalarValue(form, rowF.id);
        const colVal = readScalarValue(form, colF.id);
        if (rowVal == null || rowVal === '' || colVal == null || colVal === '') continue;
        const rowMap = getMatrixRow(action.matrix, rowVal);
        const cell = getMatrixCell(rowMap, colVal);
        if (cell != null && cell !== '') {
          const tgtDef = findFieldDefById(fields, tgt.id);
          const aligned = alignValueToSelectOptions(tgtDef, cell);
          setScalarIfChanged(form, fields, tgt.id, aligned, false);
        }
      } else if (action.kind === 'lookup') {
        const act = action as ComputedRuleLookupAction;
        const srcF = findFieldByExactFieldName(fields as any[], act.source);
        const tgtF = findFieldByExactFieldName(fields as any[], act.target);
        if (!srcF || !tgtF) continue;
        const key = readScalarValue(form, srcF.id);
        if (key == null || key === '') continue;
        const ks = String(key);
        let mapped = resolveLookupMapEntry(act.map, ks);
        mapped = coalesceLookupMapValue(mapped);
        if (mapped == null) continue;

        const tgtDef = findFieldDefById(fields, tgtF.id);

        const amountSrcName = lookupAmountSourceName(act);
        if (amountSrcName) {
          const amtF = findFieldByExactFieldName(fields as any[], amountSrcName);
          if (!amtF) continue;
          if (typeof mapped !== 'object' || mapped === null || Array.isArray(mapped)) continue;
          const entry = mapped as LookupAmountFromCuantiaEntry;
          const pct = Number(entry.percent);
          const percent = Number.isFinite(pct) ? pct : 0;
          const qty = toNumberForRanges(readScalarValue(form, amtF.id));
          const amount =
            Number.isFinite(qty) && Number.isFinite(percent)
              ? Math.round(((qty * percent) / 100) * 100) / 100
              : 0;
          setScalarIfChanged(form, fields, tgtF.id, amount, false);
          if (tgtDef) {
            const tip =
              entry.tooltip != null && String(entry.tooltip).trim() !== ''
                ? String(entry.tooltip).trim()
                : percent > 0
                  ? `Provisión ${percent}% sobre Cuantía`
                  : '';
            tgtDef.hint = tip;
          }
        } else {
          if (typeof mapped !== 'string') continue;
          const aligned = alignValueToSelectOptions(tgtDef, mapped);
          setScalarIfChanged(form, fields, tgtF.id, aligned ?? mapped, false);
        }
      }
    }
  }
}

/**
 * Nombres de campo (`field.name` exacto) cuyos cambios deben re-ejecutar las reglas.
 * - Si el bloque define `when`, se usan esos nombres.
 * - Si `when` está vacío o ausente, se infieren fuentes desde las acciones (ranges.source, matrix row/col, lookup.source).
 * - Si hay alguna acción `matrix`, también se suscribe a cada `source` de acciones `ranges` del documento: así al
 *   cambiar la cuantía se recalcula el impacto (emitEvent: false) y en la misma pasada la matriz, sin depender
 *   de valueChanges del impacto.
 */
export function collectTriggerFieldNames(doc: ComputedRulesDocument): string[] {
  const set = new Set<string>();
  const hasMatrix = (doc.rules || []).some((rule) =>
    (rule.actions || []).some((a) => a && (a as ComputedRuleAction).kind === 'matrix')
  );

  for (const rule of doc.rules || []) {
    const when = rule.when;
    if (Array.isArray(when) && when.length > 0) {
      for (const w of when) {
        if (w != null && String(w) !== '') set.add(String(w));
      }
    } else {
      for (const a of rule.actions || []) {
        if (!a || typeof a !== 'object') continue;
        if (a.kind === 'ranges') {
          const ra = a as ComputedRuleRangeAction;
          if (ra.source) set.add(String(ra.source));
        } else if (a.kind === 'matrix') {
          const ma = a as ComputedRuleMatrixAction;
          if (ma.rowSource) set.add(String(ma.rowSource));
          if (ma.colSource) set.add(String(ma.colSource));
        } else if (a.kind === 'lookup') {
          const la = a as ComputedRuleLookupAction;
          if (la.source) set.add(String(la.source));
          const amt = lookupAmountSourceName(la);
          if (amt) set.add(amt);
        }
      }
    }
  }

  if (hasMatrix) {
    for (const rule of doc.rules || []) {
      for (const a of rule.actions || []) {
        if (a?.kind === 'ranges') {
          const ra = a as ComputedRuleRangeAction;
          if (ra.source) set.add(String(ra.source));
        }
      }
    }
  }

  for (const rule of doc.rules || []) {
    for (const a of rule.actions || []) {
      if (a?.kind === 'lookup') {
        const amt = lookupAmountSourceName(a as ComputedRuleLookupAction);
        if (amt) set.add(amt);
      }
    }
  }

  return [...set];
}
